import { el } from "./dom.js";
import { navigate } from "../main.js";
import { listPicker } from "./picker.js";
import {
	listGroups,
	addItemToGroup,
	removeItemFromGroup,
	createManualGroup,
} from "../db/groups.js";

// Shared "Groups" field for any media detail view (game / book / TV / anime).
// Renders chips for each group the item is already in (each removable), plus a
// searchable "+ Add to group" picker and a "+ Create new group" shortcut.
// Membership is unconstrained — an item can sit in any number of groups — so the
// picker simply lists every group the item isn't in yet.
//
//   item     the media record (only `.id` is used)
//   groups   the groups the item is currently in (from getGroupsContainingItem)
//   onChange async callback run after any add/remove/create, so the caller can
//            re-render its detail view. Membership lives on the group records
//            (not the item), so callers typically just re-mount.
export async function groupsSection(item, groups, onChange) {
	const wrap = el("div.field", {});
	wrap.append(el("label.field__label", {}, "Groups"));

	if (groups.length === 0) {
		wrap.append(
			el("div.detail__hint", {}, el("span", {}, "Not in any group yet.")),
		);
	} else {
		const chips = el("div.chips", {});
		for (const g of groups) {
			// Outer is a div (not button) so we can nest a remove button inside.
			const chip = el(
				"div.chip.chip--active.chip--linked",
				{
					title: "Tap to view group",
					onclick: () => navigate(`/groups/${g.id}`),
				},
				el("span.chip__label", {}, g.name),
			);
			chip.append(
				el(
					"button.chip__remove",
					{
						type: "button",
						title: "Remove from group",
						onclick: async (e) => {
							e.stopPropagation();
							await removeItemFromGroup(g.id, item.id);
							await onChange();
						},
					},
					"×",
				),
			);
			chips.append(chip);
		}
		wrap.append(chips);
	}

	const allGroups = await listGroups();
	const memberIds = new Set(groups.map((g) => g.id));
	const candidates = allGroups
		.filter((g) => !memberIds.has(g.id))
		.sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));

	const createBtn = el(
		"button.btn.btn--small.picker__create",
		{
			type: "button",
			onclick: async () => {
				const name = prompt("New group name:");
				if (!name?.trim()) return;
				const newGroup = await createManualGroup({ name });
				await addItemToGroup(newGroup.id, item.id);
				await onChange();
			},
		},
		"+ Create new group",
	);

	const picker = listPicker({
		items: candidates.map((g) => ({
			id: g.id,
			name: g.name,
			subtitle: g.kind === "auto" || g.kind === "hybrid" ? "auto" : null,
		})),
		placeholder: "Search groups…",
		emptyText:
			allGroups.length === 0
				? "No groups yet — create one below."
				: "Already in every group.",
		extraAction: createBtn,
		onPick: async (picked) => {
			await addItemToGroup(picked.id, item.id);
			await onChange();
		},
	});

	const toggleBtn = el(
		"button.btn.btn--small",
		{
			type: "button",
			onclick: () => {
				picker.hidden = !picker.hidden;
				if (!picker.hidden) {
					const input = picker.querySelector(".picker__input");
					if (input) queueMicrotask(() => input.focus());
				}
			},
		},
		"+ Add to group",
	);
	wrap.append(toggleBtn, picker);
	return wrap;
}
